/*
 * Copyright (c) 2024 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, computed, forwardRef, HostBinding, inject, Input, signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CountryStoreFacade } from '@well/common-libs/country-management/state';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { remove } from 'remove-accents';
import { FloatLabel } from 'primeng/floatlabel';
import {
  BsAutocompleteComponent,
  BsFloatLabelCompatDirective
} from 'iriro-frontend-libs/bs-component-store';

@Component({
  selector: 'well-county-dropdown',
  host: { class: 'irian-custom-cva' },
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    FloatLabel,
    BsAutocompleteComponent,
    BsFloatLabelCompatDirective
  ],
  templateUrl: './county-dropdown.component.html',
  styleUrl: './county-dropdown.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountyDropdownComponent),
      multi: true,
    }
  ],
})
export class CountyDropdownComponent implements ControlValueAccessor {
  get selectedCountyName(): string | null {
    return this._selectedCountyName;
  }

  set selectedCountyName(value: string | null) {
    this.filled = !!value;
    this._selectedCountyName = value;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChangeCallback = (value: any) => {
    // No-op default implementation
  };
  private onTouchedCallback: any | undefined = undefined;
  disabled = false;
  @Input() label: string | undefined = undefined;
  @Input() floatLabelVariant: 'in' | 'over' | 'on' = 'on';

  private _selectedCountyName: string | null = null;
  private query: WritableSignal<any> = signal({ value: '' });

  @HostBinding('class.p-inputwrapper-focus') focus = false;
  @HostBinding('class.p-inputwrapper-filled') filled = false;

  private readonly countryStoreFacade = inject(CountryStoreFacade);
  counties = toSignal(this.countryStoreFacade.counties$);

  countyNames: (string | null)[] = [];

  result = computed(() => {
    const normalizedQuery = remove(this.query().value.toLowerCase());
    return this.counties()?.filter((county: any) => {
            const normalizedCountyName = remove(county.name?.toLowerCase() || '');
            return normalizedCountyName.includes(normalizedQuery);
          })?.map((county: any) => county.name);
  });

  onQueryChange(query: string) {
    this.query.set({ value: query, data: Date.now() });
  }

  onChange() {
    if (this.onChangeCallback) {
      const normalizedSelectedCountyName = remove(this.selectedCountyName?.toLowerCase() || '');

      const matchedCounty = this.countyNames.find(
        (name) => remove(name?.toLowerCase() || '') === normalizedSelectedCountyName
      );
      if (matchedCounty) {
        this.selectedCountyName = matchedCounty;
        this.onChangeCallback(this.selectedCountyName);
      } else {
        this.selectedCountyName = null;
        this.onChangeCallback(this.selectedCountyName);
      }
    }
  }

  onClear() {
    this.selectedCountyName = null;
    this.onChange();
  }

  onSelect() {
    this.onChange();
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(countyName: string | null): void {
    if (!this.countyNames.length) {
      this.countryStoreFacade.counties$.subscribe((counties: any) => {
        if (counties && counties.length) {
          this.countyNames = counties.map((county: any) => county.name);
          this.setCountyValue(countyName);
        }
      });
    } else {
      this.setCountyValue(countyName);
    }
  }

  private setCountyValue(countyName: string | null) {
    if (!countyName) {
      this.selectedCountyName = null;
    } else {
      const normalizedCountyName = remove(countyName.toLowerCase());

      const matchedCounty = this.countyNames.find((name) => remove(name?.toLowerCase() || '') === normalizedCountyName);
      if (matchedCounty) {
        this.selectedCountyName = matchedCounty;
      } else {
        this.selectedCountyName = null;
      }
    }
    this.onChange();
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  onBlur() {
    this.focus = false;
    this.onChange();
    if (this.onTouchedCallback) {
      this.onTouchedCallback(this.selectedCountyName);
    }
  }

  onInputFocus() {
    this.focus = true;
    if (this.onTouchedCallback) {
      this.onTouchedCallback(this.selectedCountyName);
    }
  }
}
